import { useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { ErrorCode, useDropzone } from "react-dropzone";

const PASTE_SHORTCUT = /Mac|iPhone|iPad/.test(navigator.userAgent) ? "⌘V" : "Ctrl+V";

export default function FileDropDialog({ title, description, accept, hint, multiple, validate, upload, onClose }: {
  title: string;
  description?: string;
  accept: string;
  hint: string;
  multiple?: boolean;
  validate: (file: File) => string | null;
  upload: (file: File) => Promise<string | null>;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ index: number; total: number } | null>(null);

  const uploadAll = async (files: File[]) => {
    setError(null);
    for (const [index, file] of files.entries()) {
      setProgress({ index, total: files.length });
      const failure = await upload(file);
      if (failure) {
        setError(failure);
        setProgress(null);
        return;
      }
    }
    setProgress(null);
    onClose();
  };

  const busy = progress !== null;

  const { getRootProps, getInputProps, isDragActive, rootRef } = useDropzone({
    accept: Object.fromEntries(accept.split(",").map((type) => [type, []])),
    multiple,
    disabled: busy,
    validator: (file) => {
      const message = validate(file);
      return message ? { code: "rejected", message } : null;
    },
    getErrorMessage: (err, file) => (
      validate(file) ?? (err.code === ErrorCode.TooManyFiles ? "Drop a single file — only one fits here" : err.message)
    ),
    onDrop: (accepted, rejections) => {
      if (rejections.length > 0) {
        setError(rejections[0].errors[0].message);
        return;
      }
      uploadAll(accepted);
    },
  });

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      slotProps={{ transition: { onEntered: () => rootRef.current?.focus() } }}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || busy) return;
        onClose();
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box
          {...getRootProps({ role: "button" })}
          sx={{
            p: 4,
            borderRadius: 1,
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "divider",
            backgroundColor: isDragActive ? "action.hover" : "transparent",
            textAlign: "center",
            cursor: busy ? "default" : "pointer",
            outlineOffset: 2,
            "&:hover": { borderColor: busy ? "divider" : "primary.main" },
          }}
        >
          <input {...getInputProps({ "aria-label": multiple ? "Choose files" : "Choose a file" })} />
          {progress ? (
            <Stack alignItems="center" spacing={1}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {progress.total > 1 ? `Uploading ${progress.index + 1} of ${progress.total}…` : "Uploading…"}
              </Typography>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={0.5}>
              <CloudUploadOutlinedIcon sx={{ fontSize: 36, color: "action.active" }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {isDragActive
                  ? "Drop to upload"
                  : multiple ? "Drag and drop files here" : "Drag and drop a file here"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {`or click to browse, or press ${PASTE_SHORTCUT} to paste`}
              </Typography>
            </Stack>
          )}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          {hint}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
